/**
 * The tenant's asset library's wire shapes (ACP-D51, GFX-D128; docs/gfx-materials/plan.md): pre-made items a designer
 * drags into a scene — PBR **materials**, **images** (flares, glows, streaks, gradients, noise, overlays, sprites),
 * HDR **environments** for reflections, **flipbooks** (smoke, fire, sparks) — in the tenant's graphics library, and the
 * stage's read-only **built-in catalog** a tenant copies them from.
 *
 * **An item** is a stable id (`itemId`, gfx's id alphabet) of one `kind` — set when it is created, never changed —
 * pointing at its current **revision**. Every save, revert and built-in copy makes a new, immutable revision naming the
 * item's gfx document as a content object (`documentSha256`: `material.json` for a material, an item document of its
 * kind for the others; gfx defines them, ACP stores them as opaque content, as it does `theme.json`) with the asset
 * files and the preview image it names. The revision is the optimistic-concurrency token: a save or revert names the
 * revision it started from (`baseRevision`), another write since is `conflict` (D111's rule for themes). A revision
 * also records the item's name, category and tags as they were, so a revert restores them with the content.
 * **Categories** are open per kind (`GRAPHICS_LIBRARY_SUGGESTED_CATEGORIES` lists the designer's): a lowercase word,
 * checked by pattern and length only.
 *
 * **The built-in catalog** is published per stage on the designer host: its index at `catalog/library/index.json`
 * (`graphicsLibraryCatalogWireSchema`) and every object it names at `catalog/content/<sha256>`. Using a built-in copies
 * its objects server-side into the tenant's content and makes a library item whose `origin` names it (D124: no
 * cross-tenant or vendor-managed sharing). A built-in's copy lives under an id derived from its `builtinId`
 * (`builtinLibraryItemId`), so asking for it again answers the same item; asking for a newer revision of it (or for the
 * same one after the tenant edited its copy) updates that item with a new revision (the explicit update of plan §1.4 —
 * the tenant's edits stay in its revision history). The item's `origin` is its lineage (the built-in it was copied
 * from, at the revision last copied); a revision's `origin` says what that revision's content is.
 */
import { z } from 'zod';
import { graphicsAssetListWireSchema, graphicsAssetWireSchema, graphicsLibraryIdWireSchema, graphicsVersionWireSchema, signedContentRequestWireSchema } from './graphics.js';
import { openEnum } from './openEnum.js';
import { isoTimestampWireSchema, sha256HexWireSchema } from './primitives.js';

/** What a library item is. An open enum on what the API answers (`unknown`: a kind a newer server added). */
export const GRAPHICS_LIBRARY_ITEM_KINDS = ['material', 'image', 'environment', 'flipbook'] as const;
/** Closed: what a writer sends. */
export const graphicsLibraryItemKindWireSchema = z.enum(GRAPHICS_LIBRARY_ITEM_KINDS);
export type GraphicsLibraryItemKindWire = z.infer<typeof graphicsLibraryItemKindWireSchema>;
/** Open: what a reader gets. */
const kindOpenWireSchema = openEnum(GRAPHICS_LIBRARY_ITEM_KINDS);
export type GraphicsLibraryItemKindOpenWire = z.infer<typeof kindOpenWireSchema>;

/** The categories the designer offers per kind. Not a closed list: any `graphicsLibraryCategoryWireSchema` word is valid. */
export const GRAPHICS_LIBRARY_SUGGESTED_CATEGORIES: { readonly [K in GraphicsLibraryItemKindWire]: readonly string[] } = {
  material: ['metal', 'wood', 'fabric', 'leather', 'stone', 'plastic', 'paint', 'glass', 'other'],
  image: ['flare', 'glow', 'streak', 'bokeh', 'gradient', 'noise', 'overlay', 'sprite', 'other'],
  environment: ['studio', 'outdoor', 'night', 'other'],
  flipbook: ['smoke', 'fire', 'spark', 'other'],
};

/** A category: one lowercase word of letters, digits and hyphens, 1-32 characters, whatever the kind. */
export const graphicsLibraryCategoryWireSchema = z.string().regex(/^[a-z][a-z0-9-]{0,31}$/, 'a category is a lowercase word (letters, digits, hyphens; at most 32)');

/** Most tags of one item. */
export const MAX_GRAPHICS_LIBRARY_TAGS = 32;
/** Most assets (texture or image files) of one item: one per slot of a lit material (plan §3) with room to spare. */
export const MAX_GRAPHICS_LIBRARY_ITEM_ASSETS = 64;

const tagSchema = z.string().trim().min(1).max(64);
const tagsSchema = z
  .array(tagSchema)
  .max(MAX_GRAPHICS_LIBRARY_TAGS)
  .refine((tags) => new Set(tags).size === tags.length, { message: 'tags must be unique' });
const nameSchema = z.string().trim().min(1).max(200);
const noteSchema = z.string().trim().max(2000);
const itemAssetsSchema = graphicsAssetListWireSchema.refine((assets) => assets.length <= MAX_GRAPHICS_LIBRARY_ITEM_ASSETS, {
  message: `an item names at most ${String(MAX_GRAPHICS_LIBRARY_ITEM_ASSETS)} assets`,
});

/**
 * A built-in's id in the catalog: lowercase words of letters, digits and hyphens joined by dots
 * (`khronos.sheen-chair.velvet-mango`). No underscore, so `builtinLibraryItemId` maps it to a library id one to one.
 */
export const graphicsBuiltinIdWireSchema = z
  .string()
  .max(128)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*(?:\.[a-z0-9]+(?:-[a-z0-9]+)*)*$/, 'a built-in id is lowercase letters, digits and hyphens in dot-separated words');

/** The library id a built-in's copy lives under: its `builtinId` with each dot an underscore (gfx's id alphabet has no dot). */
export function builtinLibraryItemId(builtinId: string): string {
  return builtinId.replaceAll('.', '_');
}

/** Where an item came from: the built-in and its catalog revision. */
export const graphicsLibraryItemOriginWireSchema = z.object({ builtinId: graphicsBuiltinIdWireSchema, revision: graphicsVersionWireSchema }).strict();
export type GraphicsLibraryItemOriginWire = z.infer<typeof graphicsLibraryItemOriginWireSchema>;

export const graphicsLibraryItemPathWireSchema = z.object({ itemId: graphicsLibraryIdWireSchema }).strict();

export const listGraphicsLibraryItemsQueryWireSchema = z
  .object({ kind: graphicsLibraryItemKindWireSchema.optional().describe('Only items of this kind (absent: every kind).') })
  .strict();
export type ListGraphicsLibraryItemsQueryWire = z.infer<typeof listGraphicsLibraryItemsQueryWireSchema>;

/** One immutable revision of a library item. */
export const graphicsLibraryItemRevisionWireSchema = z
  .object({
    itemId: graphicsLibraryIdWireSchema,
    revision: graphicsVersionWireSchema,
    /** The item's name, category and tags as this revision set them (a revert restores them). */
    name: z.string(),
    category: z.string(),
    tags: z.array(z.string()),
    /** The item's gfx document (`material.json` for a material), byte for byte; its SHA-256 is the item's identity. */
    documentSha256: sha256HexWireSchema,
    /** The files its document names, at the paths it declares. */
    assets: z.array(graphicsAssetWireSchema),
    /** The preview image's content object, or null when it has none. */
    previewSha256: sha256HexWireSchema.nullable(),
    /** The built-in this revision's content was copied from, or null (a tenant's save, or a revert to one). */
    origin: graphicsLibraryItemOriginWireSchema.nullable(),
    /** What changed, in the writer's words. */
    note: z.string().nullable(),
    createdAt: isoTimestampWireSchema,
    createdBy: z.string().nullable(),
  })
  .strict();
export type GraphicsLibraryItemRevisionWire = z.infer<typeof graphicsLibraryItemRevisionWireSchema>;

/** A library item: its kind, name, category and tags, its current revision, and the built-in it was copied from. */
export const graphicsLibraryItemWireSchema = z
  .object({
    itemId: graphicsLibraryIdWireSchema,
    kind: kindOpenWireSchema,
    name: z.string(),
    category: z.string(),
    tags: z.array(z.string()),
    /** The current revision's number: the next save or revert names it as its `baseRevision`. */
    revision: graphicsVersionWireSchema,
    current: graphicsLibraryItemRevisionWireSchema,
    /** Its lineage: the built-in it was copied from, at the catalog revision last copied, or null for the tenant's own. */
    origin: graphicsLibraryItemOriginWireSchema.nullable(),
    updatedAt: isoTimestampWireSchema,
    updatedBy: z.string().nullable(),
  })
  .strict();
export type GraphicsLibraryItemWire = z.infer<typeof graphicsLibraryItemWireSchema>;

export const listGraphicsLibraryItemsResponseWireSchema = z.object({ items: z.array(graphicsLibraryItemWireSchema) }).strict();
export type ListGraphicsLibraryItemsResponseWire = z.infer<typeof listGraphicsLibraryItemsResponseWireSchema>;

export const graphicsLibraryItemResponseWireSchema = z
  .object({
    item: graphicsLibraryItemWireSchema,
    /** Every revision, newest first. */
    revisions: z.array(graphicsLibraryItemRevisionWireSchema),
    /** A presigned GET (valid five minutes) of each content object the current revision names: its document, its assets, its preview. */
    content: z.array(signedContentRequestWireSchema),
  })
  .strict();
export type GraphicsLibraryItemResponseWire = z.infer<typeof graphicsLibraryItemResponseWireSchema>;

export const saveGraphicsLibraryItemRequestWireSchema = z
  .object({
    /** The revision this edit started from, or null to create the item (refused when it exists). */
    baseRevision: graphicsVersionWireSchema.nullable(),
    /** The item's kind: its own, once created (another is `conflict`). */
    kind: graphicsLibraryItemKindWireSchema,
    name: nameSchema,
    category: graphicsLibraryCategoryWireSchema,
    tags: tagsSchema,
    /** The item's document, uploaded first (`POST /graphics/content/uploads`). */
    documentSha256: sha256HexWireSchema,
    assets: itemAssetsSchema,
    previewSha256: sha256HexWireSchema.nullable(),
    note: noteSchema.nullable(),
  })
  .strict();
export type SaveGraphicsLibraryItemRequestWire = z.infer<typeof saveGraphicsLibraryItemRequestWireSchema>;

export const saveGraphicsLibraryItemResponseWireSchema = z.object({ item: graphicsLibraryItemWireSchema }).strict();
export type SaveGraphicsLibraryItemResponseWire = z.infer<typeof saveGraphicsLibraryItemResponseWireSchema>;

export const revertGraphicsLibraryItemRequestWireSchema = z
  .object({
    /** The current revision this revert started from; another write since is `conflict`. */
    baseRevision: graphicsVersionWireSchema,
    /** The older revision whose content, name, category and tags become the new revision's. */
    revertTo: graphicsVersionWireSchema,
    /** Null: “Reverted to revision <n>”. */
    note: noteSchema.nullable(),
  })
  .strict();
export type RevertGraphicsLibraryItemRequestWire = z.infer<typeof revertGraphicsLibraryItemRequestWireSchema>;

export const graphicsLibraryItemFromBuiltinRequestWireSchema = z
  .object({
    builtinId: graphicsBuiltinIdWireSchema,
    /** The catalog revision to copy, or null for the newest the catalog has. */
    revision: graphicsVersionWireSchema.nullable(),
  })
  .strict();
export type GraphicsLibraryItemFromBuiltinRequestWire = z.infer<typeof graphicsLibraryItemFromBuiltinRequestWireSchema>;

export const GRAPHICS_LIBRARY_FROM_BUILTIN_OUTCOMES = ['created', 'updated', 'existing'] as const;

export const graphicsLibraryItemFromBuiltinResponseWireSchema = z
  .object({
    item: graphicsLibraryItemWireSchema,
    /**
     * `created`: a new library item (or a deleted copy brought back); `updated`: the tenant's copy moved to this
     * built-in revision's content, as a new revision (it was a copy of an older revision, or edited since);
     * `existing`: the copy's current revision is this one, unedited — nothing was written.
     */
    outcome: openEnum(GRAPHICS_LIBRARY_FROM_BUILTIN_OUTCOMES),
    /** The content objects copied from the catalog into the tenant (those it lacked). */
    copied: z.array(sha256HexWireSchema),
  })
  .strict();
export type GraphicsLibraryItemFromBuiltinResponseWire = z.infer<typeof graphicsLibraryItemFromBuiltinResponseWireSchema>;

export const deleteGraphicsLibraryItemResponseWireSchema = z.object({ itemId: graphicsLibraryIdWireSchema, revision: graphicsVersionWireSchema }).strict();
export type DeleteGraphicsLibraryItemResponseWire = z.infer<typeof deleteGraphicsLibraryItemResponseWireSchema>;

// ─── the built-in catalog (published per stage on the designer host) ───

/** Where the catalog's index lives on the designer host (and in its bucket). */
export const GRAPHICS_LIBRARY_CATALOG_INDEX_KEY = 'catalog/library/index.json';
/** Where the catalog keeps each object it names, by SHA-256, on the designer host (and in its bucket). */
export function graphicsLibraryCatalogObjectKey(sha256: string): string {
  return `catalog/content/${sha256}`;
}

/** A built-in's licence (CC0 or CC-BY 4.0 only, plan §5): SPDX id, the attribution to show, the source's URL. */
export const graphicsLibraryLicenceWireSchema = z.object({ spdx: z.string().min(1).max(64), attribution: z.string().max(2000), source: z.url() }).strict();
export type GraphicsLibraryLicenceWire = z.infer<typeof graphicsLibraryLicenceWireSchema>;

export const graphicsLibraryCatalogEntryWireSchema = z
  .object({
    builtinId: graphicsBuiltinIdWireSchema,
    revision: graphicsVersionWireSchema,
    kind: kindOpenWireSchema,
    name: nameSchema,
    category: graphicsLibraryCategoryWireSchema,
    tags: z.array(tagSchema).max(MAX_GRAPHICS_LIBRARY_TAGS),
    documentSha256: sha256HexWireSchema,
    assets: itemAssetsSchema,
    previewSha256: sha256HexWireSchema.nullable(),
    licence: graphicsLibraryLicenceWireSchema.nullable(),
  })
  .strict();
export type GraphicsLibraryCatalogEntryWire = z.infer<typeof graphicsLibraryCatalogEntryWireSchema>;

/**
 * The catalog's index (`catalog/library/index.json`): every built-in the stage offers, one entry per
 * `builtinId@revision` (a catalog may keep older revisions; the newest of an id is what `revision: null` copies).
 */
export const graphicsLibraryCatalogWireSchema = z
  .object({
    catalog: z.literal(1),
    items: z
      .array(graphicsLibraryCatalogEntryWireSchema)
      .refine((entries) => new Set(entries.map((e) => `${e.builtinId}@${String(e.revision)}`)).size === entries.length, { message: 'each builtinId@revision is listed once' }),
  })
  .strict();
export type GraphicsLibraryCatalogWire = z.infer<typeof graphicsLibraryCatalogWireSchema>;
